import { useEffect, useRef, useState } from "react";
import { googleLogin } from "../../services/authApi";
import { getApiErrorMessage } from "../../services/apiClient";
import { buildUserFromAuthResponse, setAuthSession } from "../../util/auth";
import { recordGoogleLogin } from "../../util/loginHistory";
import { renderGoogleSignInButton } from "../../util/googleAuth";
import "./GoogleSignInModal.scss";

const GoogleSignInModal = ({ show = true, onHide = () => {}, onSuccess }) => {
  const buttonRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    let isMounted = true;

    const handleCredential = async (response) => {
      if (!response?.credential) {
        setErrorMessage("Google sign-in was cancelled.");
        return;
      }

      setIsSigningIn(true);
      setErrorMessage("");

      try {
        const authResponse = await googleLogin({
          credential: response.credential,
        });
        const user = buildUserFromAuthResponse(authResponse, authResponse?.user?.email);
        const sessionUser = {
          ...user,
          pictureUrl:
            authResponse.user?.pictureUrl ||
            authResponse.user?.picture ||
            user.pictureUrl,
          googleId: authResponse.user?.googleId || user.googleId,
        };

        setAuthSession(
          {
            token: authResponse.accessToken || authResponse.token,
            user: sessionUser,
          },
          true,
        );

        await recordGoogleLogin(sessionUser, { page: "/captionPublishing" });

        if (typeof onSuccess === "function") {
          onSuccess(sessionUser);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(
            getApiErrorMessage(
              error,
              "Google sign-in failed. Please try again after a hard refresh.",
            ),
          );
        }
      } finally {
        if (isMounted) {
          setIsSigningIn(false);
        }
      }
    };

    renderGoogleSignInButton(buttonRef.current, handleCredential).catch((error) => {
      if (isMounted) {
        setErrorMessage(error.message);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [onSuccess, show]);

  if (!show) {
    return null;
  }

  return (
    <div className="google-signin-modal" role="dialog" aria-modal="true" aria-labelledby="google-signin-title">
      <button
        type="button"
        className="google-signin-modal__backdrop"
        aria-label="Close sign-in dialog"
        onClick={onHide}
      />
      <div className="google-signin-modal__card">
        <button
          type="button"
          className="google-signin-modal__close"
          aria-label="Close"
          onClick={onHide}
        >
          ×
        </button>
        <h2 id="google-signin-title" className="google-signin-modal__title">
          Sign in to continue
        </h2>
        <p className="google-signin-modal__text">
          Caption Publishing requires Google Sign-In. Sign in to upload videos, generate
          subtitles, and view your upload history.
        </p>
        <div ref={buttonRef} className="google-signin-modal__button-wrap" />
        {isSigningIn && <p className="google-signin-modal__hint">Signing you in...</p>}
        {errorMessage && <p className="google-signin-modal__error">{errorMessage}</p>}
      </div>
    </div>
  );
};

export default GoogleSignInModal;
